// Packages
import React, { useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { Box, Typography, Link } from '@mui/material';
import { useSetPermissionsModal_GetGlobalVariablesQuery } from '#types';
import type { SetPermissionsModalPermissionsFragment } from '#types';

// Styles
import styles from '../PermissionsModal.module.scss';

// Markup
import { Button } from '../../../blocks/Button';
import MuiModal from '../../../mui/MuiModal/MuiModal';
import { VendorProfile, VendorProfileProps } from '../VendorProfile';
import {
	PermissionsToggles,
	PermissionsSettings,
	PermissionsTogglesProps,
} from './PermissionsToggles';
import { Icon } from '../../../blocks/icons';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getGlobalVariablesQuery = gql`
	query SetPermissionsModal_getGlobalVariables {
		getGlobalVariables {
			PlannerPermissionsHelpUrl
		}
	}
`;

const setPermissionsModalPermissionsFragment = gql`
	fragment setPermissionsModalPermissions on ClientUser {
		...permissionsTogglesPermissions
	}
	${ PermissionsToggles.fragments.permissionsTogglesPermissionsFragment }
`;

const setPermissionsModalVendorFragment = gql`
	fragment setPermissionsModalVendor on Organization {
		...vendorProfileVendor
	}
	${ VendorProfile.fragments.vendorProfileVendorFragment }
`;

export type SetPermissionsModalProps = {
	open: boolean;
	vendor: VendorProfileProps['vendor'];
	currentPermissions:
		| Omit<SetPermissionsModalPermissionsFragment, '__typename'>
		| undefined;
	permissions: PermissionsSettings;
	showPaymentText: boolean;
	setPermissions: PermissionsTogglesProps['onChange'];
	isLoading: boolean;
	onCancel: () => void;
	onConnect: ( newPermissions: PermissionsSettings ) => Promise<void>;
};

/**
 * Clientuser planner permissions modal
 */
const SetPermissionsModal = ( {
	open,
	vendor,
	currentPermissions,
	permissions,
	setPermissions,
	showPaymentText,
	isLoading,
	onCancel,
	onConnect,
}: SetPermissionsModalProps ): React.JSX.Element => {
	const [ validationError, setValidationError ] = useState<string | undefined>(
		undefined
	);
	const { data: globalVariableData } =
		useSetPermissionsModal_GetGlobalVariablesQuery();
	const plannerPermissionsHelpUrl =
		globalVariableData?.getGlobalVariables.PlannerPermissionsHelpUrl;
	const somePermissionsGranted = Object.values( permissions ).some(
		( v ) => v === true
	);
	const mode = !currentPermissions ? 'Assign' : 'Update';

	useEffect( () => {
		if ( validationError && somePermissionsGranted ) {
			setValidationError( undefined );
		}
	}, [
		validationError,
		somePermissionsGranted,
		setValidationError
	] );

	return (
		<MuiModal
			muiModalProps={ {
				open,
				onClose: onCancel,
				className: styles.permissionsModal,
			} }
			title={
				showPaymentText
					? 'Your payment has been sent.'
					: `${ mode === 'Assign' ? 'Assign' : 'Edit' } Permissions`
			}
		>
			<Box className={ styles.permissionsModalContent }>
				<VendorProfile vendor={ vendor } />
				<Typography className={ styles.description }>
					Permissions take the pressure off by giving a vendor the ability to do
					certain things on your behalf.
					{ plannerPermissionsHelpUrl ? (
						<>
							{ ' ' }
							<Link
								href={ plannerPermissionsHelpUrl }
								target='_blank'
								rel='noopener'
							>
								Learn more here
							</Link>
							.
						</>
					) : (
						'.'
					) }
				</Typography>
				<PermissionsToggles
					permissions={ permissions }
					onChange={ setPermissions }
				/>
				{ validationError && (
					<Box className={ styles.validationError }>
						<Icon type='warning-2' />
						<Typography component='span'>{ validationError }</Typography>
					</Box>
				) }
				<Box className={ styles.buttonRow }>
					<Button variant='outlined' onClick={ onCancel }>
						No thanks
					</Button>
					<Button
						variant='contained'
						loading={ isLoading }
						color='primary'
						onClick={ async () => {
							if ( !somePermissionsGranted && mode === 'Assign' ) {
								setValidationError(
									'Choose at least one permission to assign, or cancel.'
								);
							} else {
								setValidationError( undefined );
								await onConnect( permissions );
							}
						} }
					>
						{ mode }
					</Button>
				</Box>
			</Box>
		</MuiModal>
	);
};

SetPermissionsModal.fragments = {
	setPermissionsModalPermissionsFragment,
	setPermissionsModalVendorFragment,
};

export default SetPermissionsModal;
